/**
 * A tripwire on the credit copy itself: does the line we are about to render
 * agree with the credit ledger it was derived from?
 *
 * The bug this exists for was not a hard failure. Every number on screen was
 * real — "2/50 credits · resets today at 5:34 pm" — but the sentence they
 * composed was false: the account had spent 298 of a 300-credit grant, the 50
 * was a daily cap it would never reach again, and the reset would add nothing.
 * Nothing crashed, no request 500'd, and the only signal was a support message.
 *
 * So the invariants get asserted at RENDER time, where the copy actually is, and
 * a violation is reported rather than thrown: the user still sees their balance
 * (a blank credit chip is worse than a slightly wrong one), and we find out. If
 * these ever fire in production, a surface is composing its own copy instead of
 * reading `formatCreditBadge`, or an endpoint is sending a payload shape the
 * view doesn't model — both of which are exactly the class of drift the
 * "one source of truth" comment in display.ts is trying to prevent.
 */
import {
  isPoolBinding,
  willResetReplenish,
  type CreditBadge,
  type CreditView,
} from "./display";

export type CreditCopyMismatchCode =
  /**
   * The rendered line promises a reset that will not hand the user any credits
   * — the reported bug, and the one that costs us trust directly.
   */
  | "reset_promised_without_replenishment"
  /**
   * The numerator exceeds the ledger balance, i.e. the copy advertises more
   * spendable credits than the account actually holds. Every gate reads the
   * ledger, so the user would be refused at a number the UI said was fine.
   */
  | "numerator_exceeds_ledger_balance"
  /**
   * The denominator is the daily cap on an account the POOL now gates — the
   * "2/50 while 300 are spent" half of the report.
   */
  | "denominator_is_not_the_binding_limit"
  /**
   * Daily-reset copy on an account with no daily window (paid/monthly, or an
   * expired free window). Their credits arrive by renewal or top-up; a "resets
   * today" line is category-wrong, not just early.
   */
  | "daily_reset_copy_without_daily_window"
  /**
   * A paid account's denominator is not its subscription's monthly allocation —
   * the Billing-vs-chip gap this ticket was filed for. Both numbers are real, so
   * nothing looks broken: Billing reads `custom_monthly_credits` (a grandfathered
   * Pro's pinned 650) while the chip quoted `PLANS[plan].buildCreditsPerMonth`
   * (the catalog's 300). Two surfaces, two truths, no error anywhere.
   *
   * The endpoints now share lib/credits/entitlement-state, so this should be
   * permanently silent. If it fires, a surface has gone back to deriving its own
   * ceiling — which is the only way this bug can return.
   */
  | "denominator_is_not_the_entitlement"
  /**
   * A build is holding credits right now and the surface rendered its balance
   * without saying so — the reported "Today 50/50 next to 50 on hold" class of
   * confusion, in its silent form. The numbers are all real; what's missing is
   * that part of the drop is a reservation that will be partly handed back.
   *
   * Fires only where the hold is actually KNOWN (`view.onHold > 0`), so surfaces
   * fed by an endpoint that doesn't send `credits_on_hold` stay silent rather
   * than being blamed for hiding something they cannot see. The fix when it
   * fires is always the same: render lib/credits/hold.ts#formatCreditHold and
   * pass `holdDisclosed: true`.
   */
  | "hold_not_disclosed";

export interface CreditCopyMismatch {
  code: CreditCopyMismatchCode;
  /** Short, non-PII description of the disagreement, for the event payload. */
  detail: string;
}

/**
 * What the surface rendered ALONGSIDE the badge — the parts of the copy that
 * cannot be derived from `badge` because they are the caller's choice.
 */
export interface RenderedCreditCopy {
  /**
   * True when the surface rendered the active-build hold explanation
   * (lib/credits/hold.ts). Defaults to false, so a surface that has not been
   * taught about holds is flagged rather than assumed innocent — that default is
   * the whole point of the check.
   */
  holdDisclosed?: boolean;
}

/**
 * Pure comparison of rendered copy against the derived ledger view. Returns
 * every invariant the pair violates (usually none). Kept side-effect free so it
 * is trivially testable; `reportCreditCopyMismatches` does the reporting.
 */
export function detectCreditCopyMismatches(
  view: CreditView,
  badge: CreditBadge,
  rendered: RenderedCreditCopy = {},
): CreditCopyMismatch[] {
  const found: CreditCopyMismatch[] = [];

  if (badge.reset && !willResetReplenish(view)) {
    found.push({
      code: "reset_promised_without_replenishment",
      detail: `reset copy shown with 0 credits gained (available_today=${view.availableToday}, pool=${view.totalRemaining}, daily_limit=${view.dailyLimit ?? "none"})`,
    });
  }

  if (badge.reset && !view.isFreeDailyCapped) {
    found.push({
      code: "daily_reset_copy_without_daily_window",
      detail: `daily reset copy on plan=${view.plan} with no daily window`,
    });
  }

  if (badge.remaining > view.totalRemaining) {
    found.push({
      code: "numerator_exceeds_ledger_balance",
      detail: `shown=${badge.remaining} exceeds ledger balance=${view.totalRemaining}`,
    });
  }

  // Only meaningful when the two ceilings actually differ; an account whose
  // pool happens to equal its cap is quoting the same number either way.
  if (
    isPoolBinding(view) &&
    badge.basis === "daily" &&
    view.dailyLimit != null &&
    view.dailyLimit !== view.total
  ) {
    found.push({
      code: "denominator_is_not_the_binding_limit",
      detail: `denominator=${badge.limit} (daily cap) while the pool binds at ${view.totalRemaining}/${view.total}`,
    });
  }

  // A paid account rendering a pool denominator must be rendering its monthly
  // allocation. Scoped tightly so it stays silent in the states where a
  // different number is CORRECT: a top-up pushes the balance above the
  // allocation (formatCreditBadge drops the denominator, `limit` is then the
  // pool), and a trial carries the flat trial pool with no allocation at all
  // (monthlyAllocation is null there by design).
  if (
    view.plan !== "free" &&
    badge.basis === "pool" &&
    view.monthlyAllocation != null &&
    badge.limit > 0 &&
    badge.limit !== view.monthlyAllocation
  ) {
    found.push({
      code: "denominator_is_not_the_entitlement",
      detail: `denominator=${badge.limit} but plan=${view.plan} is entitled to ${view.monthlyAllocation}/month`,
    });
  }

  // A live reservation the surface didn't mention. Deliberately unconditional on
  // plan and on which balance binds: free, trial and paid accounts all reserve
  // the same way, and the confusion is worst exactly where the numbers still
  // look healthy ("Today 50/50" over a 50-credit hold).
  if (view.onHold > 0 && !rendered.holdDisclosed) {
    found.push({
      code: "hold_not_disclosed",
      detail: `${view.onHold} credits held by an unsettled build, but the rendered copy says nothing about it (shown=${badge.text})`,
    });
  }

  return found;
}

/**
 * Signatures already reported this page load. A mismatch is a property of the
 * balance state, not of the render, so a chip that re-renders on every credit
 * poll must not emit the same event dozens of times per session.
 */
const reported = new Set<string>();

/** Test seam — reset between cases so dedup state can't leak across them. */
export function __resetCreditCopyMismatchDedup(): void {
  reported.clear();
}

/**
 * Report any disagreement between `badge` and `view` for a named surface.
 * Safe to call unconditionally on every render: it de-dupes, never throws, and
 * does nothing in the overwhelmingly common case of no mismatch.
 *
 * `surface` names the credit UI doing the rendering ("nav_chip",
 * "settings_usage", "billing_plans", "builder_daily_bar", "low_credit_banner"),
 * so a regression can be traced to one component rather than to "credits".
 */
export function reportCreditCopyMismatches(
  surface: string,
  view: CreditView,
  badge: CreditBadge,
  track?: (payload: {
    surface: string;
    code: CreditCopyMismatchCode;
    detail: string;
    plan: string;
    shown_remaining: number;
    shown_limit: number;
    pool_remaining: number;
    pool_total: number;
    daily_limit: number | null;
    daily_used: number;
    available_today: number;
    credits_on_hold: number;
    hold_age_seconds: number | null;
  }) => void,
  rendered: RenderedCreditCopy = {},
): CreditCopyMismatch[] {
  const mismatches = detectCreditCopyMismatches(view, badge, rendered);
  for (const m of mismatches) {
    const signature = `${surface}:${m.code}:${badge.text}:${view.totalRemaining}`;
    if (reported.has(signature)) continue;
    reported.add(signature);
    // Loud in development, where the person who can fix it is watching.
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[credits] rendered copy disagrees with the ledger on "${surface}": ${m.code} — ${m.detail}`,
      );
    }
    try {
      track?.({
        surface,
        code: m.code,
        detail: m.detail,
        plan: view.plan,
        shown_remaining: badge.remaining,
        shown_limit: badge.limit,
        pool_remaining: view.totalRemaining,
        pool_total: view.total,
        daily_limit: view.dailyLimit,
        daily_used: view.usedToday,
        available_today: view.availableToday,
        credits_on_hold: view.onHold,
        // Server-reported age of the hold this surface was handed. The server
        // excludes reservations past HOLD_TTL_MINUTES from credits_on_hold, so
        // an age at or past the TTL in this event means the liveness bound has
        // regressed — the exact "on hold with nothing running" report.
        hold_age_seconds: view.holdAgeSeconds,
      });
    } catch {
      // Telemetry must never take a credit surface down with it.
    }
  }
  return mismatches;
}
